import React, { useEffect } from 'react';

import type * as api from '@gitbook/api-client';
import type { NavigationScreenSitesListState } from '@gitbook/app-navigation';
import {
    createPalette,
    IntlPluralize,
    PaletteButton,
    PaletteCommandType,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    BreadcrumbItem,
    Breadcrumbs,
    Button,
    Description,
    DialogRoot,
    DialogTrigger,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    HeaderBar,
    HeaderBarActions,
    HeaderBarBreadcrumbs,
    HeaderBarMain,
    Heading,
    type IconDefinition,
    PageGradientWallpaper,
    Pagination,
    TextInput,
    usePaginatedList,
} from '@gitbook/spine-react';
import { useDebouncedFlush, useStateWithDeps } from '@gitbook/util-react-hooks';

import { CreateSiteDialog, useOrganizationSites } from '../../Components';
import { useNavigation } from '../../navigation';
import { getSiteSpacesFromStructure } from '../../state';
import { SiteListCard } from './SiteListCard';

/**
 * Published status options to pick from to filter the sites list.
 */
enum SitePublishedStatus {
    All = 'all',
    Published = 'published',
    Unpublished = 'unpublished',
}

const publishedStatusOptions: {
    [key in SitePublishedStatus]: {
        value: SitePublishedStatus;
        label: string;
        icon: IconDefinition;
    };
} = {
    all: {
        value: SitePublishedStatus.All,
        label: 'All sites',
        icon: Icon.DocsSite,
    },
    published: {
        value: SitePublishedStatus.Published,
        label: 'Published',
        icon: Icon.DocsSitePublished,
    },
    unpublished: {
        value: SitePublishedStatus.Unpublished,
        label: 'Unpublished',
        icon: Icon.DocsSiteUnpublished,
    },
};

/**
 * Screen listing the sites in an organization.
 */
export function SitesListContent(props: {
    organization: api.Organization;
    organizationContent: api.OrganizationAllContent;
    screen: NavigationScreenSitesListState;
}) {
    const { organization, organizationContent, screen } = props;

    const { navigate } = useNavigation();

    // Search filter
    const searchFromScreen = screen.search ?? '';
    const [search, setSearch] = useStateWithDeps(searchFromScreen, [searchFromScreen]);
    const debounce = useDebouncedFlush(250);
    useEffect(() => {
        debounce(() => {
            navigate(
                {
                    screen: {
                        type: 'sites',
                        organization: organization.id,
                        search,
                    },
                },
                { replace: true }
            );
        });
    }, [search, navigate, organization.id, debounce]);

    // Published status filter
    const publishedStatus = screen.status ? screen.status : SitePublishedStatus.All;
    const setPublishedStatus = (value: SitePublishedStatus) => {
        navigate({
            screen: {
                type: 'sites',
                organization: organization.id,
                status: value === SitePublishedStatus.All ? undefined : value,
            },
        });
    };

    const allSites = useOrganizationSites(organizationContent, {
        published:
            publishedStatus === SitePublishedStatus.All
                ? undefined
                : publishedStatus === SitePublishedStatus.Published,
        title: searchFromScreen,
    });

    // Sort sites by modified date.
    const sortedSites = React.useMemo(() => {
        return allSites
            .map((item) => {
                return {
                    item,
                    modifiedDate: getSiteModifiedDate(item),
                };
            })
            .sort((a, b) => {
                return b.modifiedDate.date.getTime() - a.modifiedDate.date.getTime();
            });
    }, [allSites]);

    // Paginate the list to avoid performance issues when rendering iframes.
    const paginatedSites = usePaginatedList({
        label: 'sites',
        items: sortedSites,
        itemsPerPage: 6,
    });

    return (
        <>
            <HeaderBar>
                <HeaderBarMain>
                    <HeaderBarBreadcrumbs>
                        <Breadcrumbs>
                            <BreadcrumbItem icon={Icon.DocsSite} label="Docs Sites" />
                        </Breadcrumbs>
                    </HeaderBarBreadcrumbs>
                </HeaderBarMain>
                <HeaderBarActions>
                    <div className="flex items-center gap-2">
                        <TextInput
                            leadingIcon={Icon.Search}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search sites"
                        />

                        <PaletteButton
                            iconOnly
                            kind="blank"
                            leadingIcon={Icon.Filter}
                            label="Filter by"
                            palette={() =>
                                createPalette({
                                    groups: [
                                        {
                                            key: 'filter',
                                            title: 'Filter by',
                                            commands: Object.values(publishedStatusOptions).map(
                                                (option) => ({
                                                    key: option.value,
                                                    title: option.label,
                                                    icon: option.icon,
                                                    trigger: () => {
                                                        setPublishedStatus(option.value);
                                                    },
                                                    type: PaletteCommandType.RADIO,
                                                    selected: publishedStatus === option.value,
                                                })
                                            ),
                                        },
                                    ],
                                })
                            }
                            dot={publishedStatus !== SitePublishedStatus.All ? 'active' : undefined}
                        />
                    </div>
                </HeaderBarActions>
            </HeaderBar>
            <PageGradientWallpaper className="flex flex-1 flex-col items-center overflow-y-auto p-5 lg:p-12">
                {allSites.length > 0 ? (
                    <div className="flex w-full max-w-screen-lg flex-col gap-6">
                        <div className="flex flex-row items-center justify-between">
                            <div className="heading-page flex-1">
                                <p className="text-base/60">
                                    {publishedStatus === SitePublishedStatus.All
                                        ? 'All sites'
                                        : publishedStatus === SitePublishedStatus.Published
                                          ? 'Published sites'
                                          : 'Unpublished sites'}
                                </p>
                                <p>
                                    {allSites.length}{' '}
                                    <IntlPluralize
                                        count={allSites.length}
                                        singular="docs site"
                                        plural="docs sites"
                                    />
                                </p>
                            </div>

                            {organization.permissions.createContent ? (
                                <div>
                                    <DialogRoot>
                                        <DialogTrigger>
                                            <Button
                                                kind="primary"
                                                label="Create a new site"
                                                leadingIcon={Icon.Plus}
                                            />
                                        </DialogTrigger>

                                        <CreateSiteDialog organizationId={organization.id} />
                                    </DialogRoot>
                                </div>
                            ) : null}
                        </div>
                        <div
                            data-testid="sites-list"
                            className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
                        >
                            {paginatedSites.items.map(({ item, modifiedDate }) => (
                                <SiteListCard
                                    key={item.site.id}
                                    item={item}
                                    organization={organization}
                                    modifiedDate={modifiedDate}
                                />
                            ))}
                        </div>
                        <Pagination {...paginatedSites.paginationProps} />
                    </div>
                ) : (
                    <SiteListEmptyStateWithFilters
                        onClear={() => {
                            navigate({
                                screen: {
                                    type: 'sites',
                                    organization: organization.id,
                                    status: undefined,
                                    search: '',
                                },
                            });
                        }}
                    />
                )}
            </PageGradientWallpaper>
        </>
    );
}

function SiteListEmptyStateWithFilters(props: { onClear: () => void }) {
    return (
        <div className="flex-1 flex-row items-center justify-center">
            <EmptyState>
                <EmptyStateIcon>
                    <Icon.DocsSite />
                </EmptyStateIcon>
                <Heading>No sites found matching your filters</Heading>
                <Description>
                    Try changing your search query or published status filter.
                </Description>
                <EmptyStateActions>
                    <Button kind="blank" label="Clear filters" onPress={props.onClear} />
                </EmptyStateActions>
            </EmptyState>
        </div>
    );
}

function getSiteModifiedDate(item: api.OrganizationAllSite) {
    const dates: Array<{
        date: Date;
        type: 'created' | 'updated';
    }> = [];

    dates.push({ date: new Date(item.site.createdAt), type: 'created' });

    getSiteSpacesFromStructure(item.structure).forEach((siteSpace) => {
        dates.push({ date: new Date(siteSpace.space.updatedAt), type: 'updated' });
    });

    return dates.sort((a, b) => a.date.getTime() - b.date.getTime())[0];
}
